import React, { useState } from 'react';
import { Alert, Button, Box, IconButton, Collapse } from '@mui/material';
import { Close as CloseIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
import { useFirebaseMessaging } from '@/shared/hooks/useFirebaseMessaging';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface NotificationPermissionBannerProps {
  onPermissionGranted?: () => void;
  onPermissionDenied?: () => void;
}

export const NotificationPermissionBanner: React.FC<NotificationPermissionBannerProps> = ({
  onPermissionGranted,
  onPermissionDenied
}) => {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);
  const [requesting, setRequesting] = useState(false);

  const { notificationPermission, isSupported, requestPermissionAndInit } = useFirebaseMessaging();

  const handleRequestPermission = async () => {
    setRequesting(true);

    try {
      // Use Safari-compatible method that requests permission and initializes in one call
      const granted = await requestPermissionAndInit();

      if (granted) {
        onPermissionGranted?.();
        setDismissed(true);
      } else {
        onPermissionDenied?.();
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      onPermissionDenied?.();
    } finally {
      setRequesting(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    onPermissionDenied?.();
  };

  // Don't show banner if:
  // - Not supported
  // - Already granted permission
  // - User dismissed it
  // - Permission was denied
  if (
    !isSupported ||
    notificationPermission === 'granted' ||
    notificationPermission === 'denied' ||
    dismissed
  ) {
    return null;
  }

  return (
    <Collapse in={!dismissed}>
      <Box sx={{ mb: 2 }}>
        <Alert
          icon={<NotificationsIcon />}
          severity="info"
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                color="inherit"
                size="small"
                onClick={handleRequestPermission}
                disabled={requesting}
                variant="outlined"
              >
                {requesting
                  ? t('SHARED.NOTIFICATION_PERMISSION_BANNER.REQUESTING_BUTTON')
                  : t('SHARED.NOTIFICATION_PERMISSION_BANNER.ALLOW_BUTTON')}
              </Button>
              <IconButton
                aria-label={t('SHARED.NOTIFICATION_PERMISSION_BANNER.CLOSE_ARIA_LABEL')}
                color="inherit"
                size="small"
                onClick={handleDismiss}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Box>
          }
        >
          <Typography variant="body2" sx={{ fontSize: '1.8rem' }}>
            <strong>{t('SHARED.NOTIFICATION_PERMISSION_BANNER.TITLE')}</strong>{' '}
            {t('SHARED.NOTIFICATION_PERMISSION_BANNER.MESSAGE')}
          </Typography>
        </Alert>
      </Box>
    </Collapse>
  );
};

export default NotificationPermissionBanner;
